/**
 * Préférences utilisateur — chargement, effets globaux (largeur de page, thème) et
 * enregistrement.
 *
 * Le serveur est la source de vérité (§ chantier préférences, 2026-09-15) : cette même
 * fonction est appelée depuis `AppShell` (pour appliquer la largeur/le thème dès la session
 * ouverte, sur toutes les pages) et depuis `PreferencesPage` (pour l'écran de réglages
 * lui-même). Pas de couche de cache partagée entre les deux instances — même philosophie que
 * `useAsync` : l'application est petite, une deuxième requête réseau coûte moins qu'un magasin
 * partagé de plus.
 *
 * `PUT /api/me/preferences` exige un corps COMPLET (pas de correctif partiel) : `save` fusionne
 * toujours sur l'état déjà connu avant d'envoyer.
 *
 * Deux garde-fous, posés après l'audit de sécurité du 2026-09-15 :
 *
 * 1. **L'échec d'écriture ne peut pas être silencieux.** L'application est optimiste, donc un refus
 *    du serveur doit défaire l'affichage ET se dire. Sans cela, un 403 de la garde d'origine ou un
 *    429 du limiteur laisserait l'écran afficher « enregistré » — et masquerait en production le
 *    signal même qu'on veut voir si la protection CSRF se déclenche à tort.
 * 2. **Les écritures sont regroupées.** L'écran applique chaque réglage au clic, et un
 *    `<input type="time">` manipulé aux flèches émet une écriture par incrément : sans
 *    regroupement, un utilisateur épuise son propre quota d'API en réglant ses heures calmes,
 *    puis reçoit des 429 sur le reste du produit. L'affichage reste immédiat ; seul l'envoi attend.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { PREFERENCE_DEFAULTS, pageWidthToCss } from '@exiplan/shared-types';

import { api } from '../lib/api.js';
import { useAsync } from './useAsync.js';
import { useTheme } from './useTheme.js';

/**
 * Délai de regroupement des envois, en millisecondes. 400 ms : assez pour absorber une rafale de
 * flèches sur un champ horaire, assez court pour qu'un clic isolé reste perçu comme immédiat.
 */
const DELAI_REGROUPEMENT_MS = 400;

export function usePreferences() {
  const { setTheme } = useTheme();
  const { data, loading, error, reload } = useAsync(
    (signal) => api.get('/me/preferences', { signal }),
    [],
  );

  const [preferences, setPreferences] = useState(PREFERENCE_DEFAULTS);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Regroupement des envois : `enAttente` porte l'état à envoyer, `minuterie` l'échéance.
  // `dernierConnu` garde le dernier état confirmé par le serveur, pour pouvoir revenir en
  // arrière si l'envoi échoue — l'état affiché, lui, a déjà avancé.
  const enAttente = useRef(null);
  const minuterie = useRef(null);
  const dernierConnu = useRef(PREFERENCE_DEFAULTS);

  useEffect(() => {
    // Garde de FORME, pas seulement de véracité : un tableau ou une chaîne est truthy, et le
    // destructuring en tirerait un objet vide qu'on prendrait pour des préférences valides.
    // C'est exactement ce qu'un mock de test trop permissif a produit.
    if (!data?.data || typeof data.data !== 'object' || Array.isArray(data.data)) return;
    const { updatedAt: serverUpdatedAt, ...rest } = data.data;
    const confirme = { ...PREFERENCE_DEFAULTS, ...rest };
    setPreferences(confirme);
    dernierConnu.current = confirme;
    setUpdatedAt(serverUpdatedAt ?? null);
  }, [data]);

  // Largeur de page : posée sur <html> dès que les préférences (ou leurs défauts) sont connues,
  // pour que toute page — pas seulement /preferences — en profite immédiatement.
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--largeur-corps',
      pageWidthToCss(preferences.pageWidth),
    );
  }, [preferences.pageWidth]);

  // Thème : n'écrase le miroir localStorage / anti-flash qu'une fois la réponse serveur connue.
  useEffect(() => {
    if (!data?.data) return;
    setTheme(preferences.theme);
  }, [data, preferences.theme]);

  /** Envoie l'état en attente. Le corps est COMPLET — le serveur refuse un correctif partiel. */
  const envoyer = useCallback(async () => {
    const aEnvoyer = enAttente.current;
    if (!aEnvoyer) return;
    enAttente.current = null;
    setSaving(true);
    setSaved(false);
    setSaveError(null);
    try {
      const response = await api.put('/me/preferences', aEnvoyer);
      const { updatedAt: serverUpdatedAt, ...rest } = response.data;
      const confirme = { ...PREFERENCE_DEFAULTS, ...rest };
      setPreferences(confirme);
      dernierConnu.current = confirme;
      setUpdatedAt(serverUpdatedAt ?? null);
      setSaved(true);
    } catch (erreur) {
      // Retour à l'état confirmé par le serveur : laisser l'affichage sur une valeur refusée
      // ferait croire à un enregistrement qui n'a pas eu lieu.
      setPreferences(dernierConnu.current);
      setTheme(dernierConnu.current.theme);
      setSaveError(erreur);
    } finally {
      setSaving(false);
    }
  }, [setTheme]);

  /** @param {Partial<typeof PREFERENCE_DEFAULTS>} partial */
  const save = useCallback(
    (partial) => {
      const next = { ...(enAttente.current ?? preferences), ...partial };
      // Application immédiate, avant même la réponse réseau : c'est le comportement demandé,
      // pas de bouton « Enregistrer ».
      setPreferences(next);
      if (partial.theme) setTheme(partial.theme);
      enAttente.current = next;
      setSaved(false);
      setSaveError(null);
      if (minuterie.current) clearTimeout(minuterie.current);
      minuterie.current = setTimeout(envoyer, DELAI_REGROUPEMENT_MS);
    },
    [preferences, setTheme, envoyer],
  );

  // Un réglage posé juste avant de quitter la page ne doit pas être perdu dans la minuterie.
  useEffect(
    () => () => {
      if (minuterie.current) clearTimeout(minuterie.current);
      if (enAttente.current) envoyer();
    },
    [envoyer],
  );

  return { preferences, updatedAt, loading, error, saving, saved, saveError, save, reload };
}
